import { useState } from 'react'
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  CardHeader,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  ClickAwayListener,
  Stack,
  Typography,
} from '@mui/material'
import { ArrowDropDown, Download } from '@mui/icons-material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useSnackbar } from 'notistack'
import { useCurrentUser } from '~/common/use-current-user'
import { useFeatures } from '~/common/use-features'
import { useDataExport } from './use-data-export'

const FORMAT_OPTIONS = [
  { value: 'csv', label: 'CSV' },
  { value: 'json', label: 'JSON' },
]

export const AccountDataExportCard = () => {
  const { currentUser } = useCurrentUser()
  const { features } = useFeatures()
  const { enqueueSnackbar } = useSnackbar()
  const { handleExport, exporting } = useDataExport()

  // Derive default start date from user creation date; fall back to 1 year ago
  const defaultStart = currentUser?.created_at ? dayjs(currentUser.created_at) : dayjs().subtract(1, 'year').startOf('year')
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(dayjs())
  const [format, setFormat] = useState('csv')
  const [menuAnchor, setMenuAnchor] = useState(null)

  if (!features['export_data']) return null

  const effectiveStart = startDate ?? defaultStart

  const onExport = async () => {
    if (endDate.isBefore(effectiveStart)) {
      enqueueSnackbar('End date must be after start date.', { variant: 'warning' })
      return
    }
    try {
      await handleExport(format, { startDate: effectiveStart, endDate })
      enqueueSnackbar('Data exported successfully.', { variant: 'success' })
    } catch (err) {
      enqueueSnackbar(err.message, { variant: 'error' })
    }
  }

  const selectedLabel = FORMAT_OPTIONS.find(o => o.value === format)?.label ?? 'CSV'

  return (
    <Card variant="outlined">
      <CardHeader title="Data Export" sx={{ pb: 0 }} />
      <CardContent>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack spacing={2}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
                columnGap: 2,
                rowGap: { xs: 0.5, md: 0 },
                alignItems: 'flex-start',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Export your TerraPlanner data within a date range. Includes all crops, schedules,
                phases, tasks, reports, and organization memberships across your farms.
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
                columnGap: 2,
                rowGap: { xs: 1.5, md: 0 },
                alignItems: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Start date for the export range.
              </Typography>
              <DatePicker
                label="Start date"
                value={effectiveStart}
                onChange={(val) => val && setStartDate(val)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
                columnGap: 2,
                rowGap: { xs: 1.5, md: 0 },
                alignItems: 'center',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                End date for the export range.
              </Typography>
              <DatePicker
                label="End date"
                value={endDate}
                onChange={(val) => val && setEndDate(val)}
                minDate={effectiveStart}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <ButtonGroup variant="outlined" disabled={exporting}>
                <Button
                  startIcon={<Download />}
                  onClick={onExport}
                  sx={{ textTransform: 'none' }}
                >
                  {exporting ? 'Exporting...' : `Export as ${selectedLabel}`}
                </Button>
                <Button
                  onClick={(e) => setMenuAnchor(menuAnchor ? null : e.currentTarget)}
                  sx={{ px: 0.5 }}
                >
                  <ArrowDropDown />
                </Button>
              </ButtonGroup>
              <Popper open={Boolean(menuAnchor)} anchorEl={menuAnchor} placement="bottom-end" sx={{ zIndex: 1300 }}>
                <Paper elevation={3}>
                  <ClickAwayListener onClickAway={() => setMenuAnchor(null)}>
                    <MenuList dense>
                      {FORMAT_OPTIONS.map((opt) => (
                        <MenuItem
                          key={opt.value}
                          selected={opt.value === format}
                          onClick={() => { setFormat(opt.value); setMenuAnchor(null) }}
                        >
                          {opt.label}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Popper>
            </Box>
          </Stack>
        </LocalizationProvider>
      </CardContent>
    </Card>
  )
}
